import { jsx } from '@amazon/vinyl-tsx'
import type { ObservableValue } from '@amazon/vinyl-observable'
import type { I18nString } from '@amzn/bf-i18n'
import { Link } from '@/components/Link'
import type { NavigateOptions } from '@/router/router'

type LocalizedText = ObservableValue<I18nString | ''>

// GH's ErrorContent variants share this cover graphic.
const ERROR_HEADPHONES_LOGO_URL =
    'https://m.media-amazon.com/images/G/01/digital/music/player/recs-error-headphones'

export type ErrorSurfaceAction =
    | {
          readonly href: string
          readonly label: LocalizedText
          readonly navigateOptions?: NavigateOptions
      }
    | { readonly label: LocalizedText; readonly onClick: () => void }

export interface ErrorSurfaceProps {
    readonly message: LocalizedText
    readonly detail?: LocalizedText
    readonly actions: readonly ErrorSurfaceAction[]
}

/**
 * Full-page error/empty surface — GH's ErrorContent shape (message, optional
 * detail line, action links/buttons, graphic). Shared by NotFoundPage (404)
 * and the generic ErrorPage (route/load-failure recovery).
 */
export function ErrorSurface(props: ErrorSurfaceProps) {
    return (
        <div className="errorSurface">
            <p className="errorSurfaceMessage">{props.message}</p>
            {props.detail != null ? <p className="errorSurfaceDetail">{props.detail}</p> : null}
            {...props.actions.map(renderAction)}
            <img
                className="errorSurfaceImage"
                src={ERROR_HEADPHONES_LOGO_URL}
                alt=""
                aria-hidden="true"
            />
        </div>
    )
}

function renderAction(action: ErrorSurfaceAction) {
    if ('href' in action) {
        return (
            <Link
                className="errorSurfaceAction textLink"
                href={action.href}
                {...(action.navigateOptions ? { navigateOptions: action.navigateOptions } : null)}
            >
                {action.label}
            </Link>
        )
    }
    return (
        <button className="errorSurfaceAction textLink" onclick={action.onClick}>
            {action.label}
        </button>
    )
}
